"use client";
import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const colors = [
  {
    name: "Brick",
    tileImage: "/images/home/available-colors/brick.png",
    exampleImage: "/images/home/available-colors/brick-example.jpeg",
  },
  {
    name: "Downing Sands",
    tileImage: "/images/home/available-colors/downing-sands.png",
    exampleImage: "/images/home/available-colors/downing-sands-example.jpeg",
  },
  {
    name: "WhirlyBird",
    tileImage: "/images/home/available-colors/whirlybird.png",
    exampleImage: "/images/home/available-colors/whirlybird-example.jpeg",
  },
  {
    name: "Golden Harvest",
    tileImage: "/images/home/available-colors/golden-harvest.png",
    exampleImage: "/images/home/available-colors/golden-harvest-example.jpeg",
  },
  {
    name: "Super Nova",
    tileImage: "/images/home/available-colors/super-nova.png",
    exampleImage: "/images/home/available-colors/super-nova-example.jpeg",
  },
  {
    name: "Gray Wool",
    tileImage: "/images/home/available-colors/gray-wool.png",
    exampleImage: "/images/home/available-colors/gray-wool-example.jpeg",
  },
  {
    name: "Light Taupe",
    tileImage: "/images/home/available-colors/light-taupe.png",
    exampleImage: "/images/home/available-colors/light-taupe-example.jpeg",
  },
  {
    name: "Terracotta",
    tileImage: "/images/home/available-colors/terracotta.png",
    exampleImage: "/images/home/available-colors/terracotta-example.jpeg",
  },
  {
    name: "Dark Jade",
    tileImage: "/images/home/available-colors/dark-jade.png",
    exampleImage: "/images/home/available-colors/dark-jade-example.jpeg",
  },
];

export function ColorShowcaseSection() {
  return (
    <section
      id="home-colors"
      className="py-24 bg-foreground/5"
      data-header-scheme="light"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-serif text-4xl md:text-5xl font-light text-foreground mb-6 tracking-tight">
            Available Colors
          </h2>
          <p className="font-sans text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Choose from our curated palette of premium microcement colors, each
            designed to complement modern architectural aesthetics
          </p>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {colors.map((color, index) => (
            <motion.div
              key={color.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.05 }}
              viewport={{ once: true }}
              className="group relative cursor-pointer"
            >
              <div className="absolute -top-14 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10 pointer-events-none">
                <div className="bg-popover backdrop-blur-sm text-popover-foreground px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap shadow-xl border border-border font-sans dark:bg-stone-800/95 dark:text-foreground">
                  {color.name}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-popover dark:border-t-stone-800/95" />
                </div>
              </div>
              <div className="bg-card rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-border relative">
                <div className="flex h-48">
                  <div className="w-1/2 relative">
                    <Image
                      src={color.tileImage}
                      alt={`${color.name} Tile Sample`}
                      fill
                      sizes="300px"
                      className="object-cover"
                    />
                  </div>
                  <div className="w-1/2 relative">
                    <Image
                      src={color.exampleImage}
                      alt={`${color.name} Usage Example`}
                      fill
                      sizes="300px"
                      className="object-cover"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <Button size="lg" asChild>
            <Link href="/concretta">
              View All Options <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>
  );
}
